import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Assignment } from '@mui/icons-material';
import { COVENANTS_DATE_TIME_FORMAT } from '~/modules/covenants/constants.tsx';
import {
  MANAGER_ALL_STATUSES_LABEL,
  MANAGER_APPLICATION_EMPTY_TEXT,
  MANAGER_APPLICATION_EMPTY_VALUE,
  MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS,
  MANAGER_APPLICATION_SORTABLE_COLUMNS,
  MANAGER_APPLICATION_TABLE_TITLE,
  MANAGER_ALL_SEGMENTS_LABEL,
  MANAGER_ASSIGNEE_COLUMN,
  MANAGER_STATUS_COLUMN,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import { useManagerApplicationsTable } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useManagerApplicationsTable.ts';
import type {
  TManagerApplicationFilters,
  TManagerApplicationRow,
  TManagerSegmentOption,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import {
  getManagerAssigneeFilterTooltipTitle,
  getManagerSegmentFilterTooltipTitle,
  getManagerStatusFilterTooltipTitle,
  isManagerAssigneeMatch,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import { formatDate } from '~/modules/covenants/utils.ts';
import { ActionsMenu } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/ActionsMenu.tsx';
import { AssigneeFilterMenuItems } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/AssigneeFilterMenuItems.tsx';
import { FilterMenuItems } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/FilterMenuItems.tsx';
import { IconFilter } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/IconFilter.tsx';
import { SortableHeader } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/SortableHeader.tsx';
import { StatusChip } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/StatusChip.tsx';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';

interface IManagerApplicationsTableProps {
  applications: TManagerApplicationRow[];
  assignedByCurrentManagerIds: string[];
  currentManagerIdentityValues: string[];
  filters: TManagerApplicationFilters;
  totalElements: number;
  isLoading: boolean;
  segmentOptions: TManagerSegmentOption[];
  activeAssignmentAction: {
    applicationId: string;
    action: 'assign' | 'unassign';
  } | null;
  onAssignApplication: (application: TManagerApplicationRow) => void;
  onUnassignApplication: (application: TManagerApplicationRow) => void;
  onViewApplication: (application: TManagerApplicationRow) => void;
  onOpenCovenants: (application: TManagerApplicationRow) => void;
  onFiltersChange: (
    updater: (previousState: TManagerApplicationFilters) => TManagerApplicationFilters
  ) => void;
}

const getManagerHeaderAlignment = (columnIndex: number, totalColumns: number) => {
  if (columnIndex === 0) {
    return 'left';
  }

  if (columnIndex === totalColumns - 1) {
    return 'right';
  }

  return 'center';
};

export const ManagerApplicationsTable: React.FC<IManagerApplicationsTableProps> = ({
  applications,
  assignedByCurrentManagerIds,
  currentManagerIdentityValues,
  filters,
  totalElements,
  isLoading,
  segmentOptions,
  activeAssignmentAction,
  onAssignApplication,
  onUnassignApplication,
  onViewApplication,
  onOpenCovenants,
  onFiltersChange,
}) => {
  const totalColumns = 8;

  const {
    assigneeOptions,
    isStatusFilterActive,
    isAssigneeFilterActive,
    isSegmentFilterActive,
    handleStatusFilterChange,
    handleAssigneeFilterChange,
    handleSegmentFilterChange,
    handleSortToggle,
    handlePageChange,
    handleRowsPerPageChange,
  } = useManagerApplicationsTable({
    applications,
    filters,
    onFiltersChange,
  });

  return (
    <Paper elevation={0} sx={managerStyles.tablePaper}>
      <Box sx={managerStyles.tableToolbar}>
        <Box sx={managerStyles.tableToolbarContent}>
          <Assignment color="secondary" />
          <Typography variant="h6" sx={managerStyles.tableTitle}>
            {MANAGER_APPLICATION_TABLE_TITLE}
          </Typography>
        </Box>
      </Box>

      <Box sx={commonStyles.loadingWrap}>
        <TableContainer sx={managerStyles.tableContainer}>
          <Table sx={managerStyles.tableRoot}>
            <TableHead sx={managerStyles.tableHead}>
              <TableRow sx={managerStyles.tableHeaderRow}>
                <TableCell
                  align={getManagerHeaderAlignment(0, totalColumns)}
                  sx={managerStyles.tableHeaderCell}
                >
                  <Box sx={managerStyles.tableHeaderFilter}>
                    Сегмент
                    <IconFilter
                      tooltipTitle={getManagerSegmentFilterTooltipTitle(filters.segmentIds)}
                      isActive={isSegmentFilterActive}
                      renderContent={(closeMenu) => (
                        <FilterMenuItems
                          allLabel={MANAGER_ALL_SEGMENTS_LABEL}
                          options={segmentOptions}
                          selectedValues={filters.segmentIds}
                          onSelectValues={handleSegmentFilterChange}
                          closeMenu={closeMenu}
                        />
                      )}
                    />
                  </Box>
                </TableCell>
                {MANAGER_APPLICATION_SORTABLE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.sortKey}
                    align={getManagerHeaderAlignment(
                      MANAGER_APPLICATION_SORTABLE_COLUMNS.indexOf(column) + 1,
                      totalColumns
                    )}
                    sx={managerStyles.tableHeaderCell}
                  >
                    <SortableHeader
                      label={column.label}
                      sortKey={column.sortKey}
                      activeSortKey={filters.sortKey}
                      direction={filters.sortDirection}
                      onToggle={handleSortToggle}
                    />
                  </TableCell>
                ))}
                <TableCell
                  align={getManagerHeaderAlignment(5, totalColumns)}
                  sx={managerStyles.tableHeaderCell}
                >
                  <Box sx={managerStyles.tableHeaderFilter}>
                    <SortableHeader
                      label={MANAGER_STATUS_COLUMN.label}
                      sortKey={MANAGER_STATUS_COLUMN.sortKey}
                      activeSortKey={filters.sortKey}
                      direction={filters.sortDirection}
                      onToggle={handleSortToggle}
                    />
                    <IconFilter
                      tooltipTitle={getManagerStatusFilterTooltipTitle(filters.status)}
                      isActive={isStatusFilterActive}
                      renderContent={(closeMenu) => (
                        <FilterMenuItems
                          allLabel={MANAGER_ALL_STATUSES_LABEL}
                          optionPreset="manager-status"
                          selectedValues={filters.status}
                          onSelectValues={handleStatusFilterChange}
                          closeMenu={closeMenu}
                        />
                      )}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align={getManagerHeaderAlignment(6, totalColumns)}
                  sx={managerStyles.tableHeaderCell}
                >
                  <Box sx={managerStyles.tableHeaderFilter}>
                    <SortableHeader
                      label={MANAGER_ASSIGNEE_COLUMN.label}
                      sortKey={MANAGER_ASSIGNEE_COLUMN.sortKey}
                      activeSortKey={filters.sortKey}
                      direction={filters.sortDirection}
                      onToggle={handleSortToggle}
                    />
                    <IconFilter
                      tooltipTitle={getManagerAssigneeFilterTooltipTitle(filters.assignee)}
                      isActive={isAssigneeFilterActive}
                      renderContent={(closeMenu) => (
                        <AssigneeFilterMenuItems
                          assigneeOptions={assigneeOptions}
                          selectedAssignee={filters.assignee}
                          onSelectAssignee={handleAssigneeFilterChange}
                          closeMenu={closeMenu}
                        />
                      )}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align={getManagerHeaderAlignment(7, totalColumns)}
                  sx={managerStyles.tableHeaderCell}
                >
                  Дія
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!applications.length ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={dataFormStyles.emptyCell}>
                    {MANAGER_APPLICATION_EMPTY_TEXT}
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application) => {
                  const isAssignedToCurrentManager =
                    assignedByCurrentManagerIds.includes(application.id) ||
                    isManagerAssigneeMatch(application.assignee, currentManagerIdentityValues);

                  return (
                    <TableRow key={application.id} hover sx={managerStyles.tableRow}>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.segmentName || MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.contragentCode || MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.contragentShortName || MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.contragentFullName || MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.sentAt
                          ? formatDate(application.sentAt, COVENANTS_DATE_TIME_FORMAT)
                          : MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        <StatusChip status={application.status} />
                      </TableCell>
                      <TableCell sx={managerStyles.tableBodyCell}>
                        {application.assignee || MANAGER_APPLICATION_EMPTY_VALUE}
                      </TableCell>
                      <TableCell align="center" sx={managerStyles.tableBodyCell}>
                        <ActionsMenu
                          application={application}
                          isAssignedToCurrentManager={isAssignedToCurrentManager}
                          activeAssignmentAction={activeAssignmentAction}
                          onAssignApplication={onAssignApplication}
                          onUnassignApplication={onUnassignApplication}
                          onViewApplication={onViewApplication}
                          onOpenCovenants={onOpenCovenants}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && (
          <Box sx={commonStyles.loadingMask}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      <Box sx={managerStyles.tablePagination}>
        <TablePaginationControls
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          totalElements={totalElements}
          isLoading={isLoading}
          onPageChange={(nextPage) => handlePageChange(null, nextPage)}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS}
        />
      </Box>
    </Paper>
  );
};

export default ManagerApplicationsTable;
